import { lazy, ComponentType } from 'react';

type Factory<T> = () => Promise<{ default: T }>;

interface Options {
  retries?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  componentName?: string; // For better debugging
}

// Detect typical dynamic import/chunk-load errors that can be recovered by a hard reload
function isRecoverableChunkError(err: unknown): boolean {
  const msg = (err && (err as any).message) ? String((err as any).message) : '';
  const name = (err && (err as any).name) ? String((err as any).name) : '';
  const stack = (err && (err as any).stack) ? String((err as any).stack) : '';

  return (
    name === 'ChunkLoadError' ||
    name === 'TypeError' ||
    /Loading chunk .* failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Expected a (JavaScript|module) .* MIME type/i.test(msg) ||
    /script has an unsupported MIME type/i.test(msg) ||
    /Failed to load module script/i.test(msg) ||
    /NetworkError|fetch/i.test(msg) ||
    /import\(\)/i.test(stack) // Catches dynamic import errors
  );
}

// Check if we're likely experiencing a deployment race condition
function isDeploymentRace(): boolean {
  // If the page was loaded recently (within 5 minutes), it might be a deployment issue
  const pageLoadTime = performance?.timing?.navigationStart;
  if (pageLoadTime) {
    const timeSinceLoad = Date.now() - pageLoadTime;
    return timeSinceLoad < 5 * 60 * 1000; // 5 minutes
  }
  return false;
}

function forceReloadOnce(marker: string, componentName?: string) {
  try {
    const reloadKey = `${marker}_${componentName || 'unknown'}`;
    const reloadCount = parseInt(sessionStorage.getItem(reloadKey) || '0', 10);
    
    // Allow up to 2 reload attempts per component
    if (reloadCount < 2) {
      sessionStorage.setItem(reloadKey, String(reloadCount + 1));
      
      // Set a timestamp to track when we last reloaded
      sessionStorage.setItem(`${reloadKey}_timestamp`, String(Date.now()));
      
      console.warn(`[lazyWithRetry] Reloading page due to chunk error (attempt ${reloadCount + 1}) for: ${componentName || 'unknown component'}`);
      
      // Small delay to ensure error is logged
      setTimeout(() => window.location.reload(), 100);
      return true;
    } else {
      // Clear old reload attempts after 10 minutes
      const lastReloadTime = parseInt(sessionStorage.getItem(`${reloadKey}_timestamp`) || '0', 10);
      if (Date.now() - lastReloadTime > 10 * 60 * 1000) {
        sessionStorage.removeItem(reloadKey);
        sessionStorage.removeItem(`${reloadKey}_timestamp`);
      }
    }
  } catch {
    // If sessionStorage is unavailable, still attempt one reload
    console.warn('[lazyWithRetry] SessionStorage unavailable, attempting reload');
    window.location.reload();
    return true;
  }
  return false;
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: Factory<T>, 
  options?: Options
): React.LazyExoticComponent<T> {
  const retries = Math.max(0, options?.retries ?? 3);
  const initialDelayMs = Math.max(100, options?.initialDelayMs ?? 500);
  const maxDelayMs = Math.max(initialDelayMs, options?.maxDelayMs ?? 5000);
  const componentName = options?.componentName;

  return lazy(async () => {
    let lastErr: unknown | null = null;
    let delayMs = initialDelayMs;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Add a small random jitter to prevent thundering herd
        const jitter = Math.random() * 100;
        
        if (attempt > 0) {
          console.log(`[lazyWithRetry] Retry attempt ${attempt}/${retries} for: ${componentName || 'component'}`);
          await new Promise((r) => setTimeout(r, delayMs + jitter));
        }
        
        return await factory();
      } catch (err) {
        lastErr = err;
        console.error(`[lazyWithRetry] Failed to load ${componentName || 'component'}:`, err);

        if (isRecoverableChunkError(err)) {
          // For deployment races, reload more aggressively
          if (isDeploymentRace()) {
            const reloaded = forceReloadOnce('__deployment_race__', componentName);
            if (reloaded) {
              // Wait for reload to happen
              await new Promise((r) => setTimeout(r, 5000));
            }
          }
          
          // For other chunk errors, try reloading after exhausting retries
          if (attempt === retries) {
            const reloaded = forceReloadOnce('__lazy_chunk_reload__', componentName);
            if (reloaded) {
              // Wait for reload to happen
              await new Promise((r) => setTimeout(r, 5000));
            }
          }
        }

        // If not the last attempt, increase delay with exponential backoff
        if (attempt < retries) {
          delayMs = Math.min(delayMs * 2, maxDelayMs);
          continue;
        }

        // Exhausted retries: surface the original error
        throw err;
      }
    }

    // Should never reach here; throw the last captured error defensively
    throw lastErr ?? new Error(`Unknown lazy import error for: ${componentName || 'unknown component'}`);
  });
}

export default lazyWithRetry;

